// Activity events, Mirinae's own record of what learners do (docs/audience-analytics/activity-events.md).
// Two pieces: every request to our own /api carries the platform (web, ios or android) and the app
// version, so the server can record which one each action came from; and sendEvent reports the few
// actions only the browser sees. Nothing here goes to any third party.
import axios from 'axios';

import { os } from '@mirinae/js-utils/utils/userAgent';
import { useAppVersionStore } from '@mirinae/stores/appVersion';

const isOwnApi = (url) => typeof url === 'string' && url.startsWith('/api/');

// web, ios or android ('' when an app on another system); read when needed, since app detection can
// finish (or be corrected) after the first requests
export const currentPlatform = () => {
    const { isApp } = useAppVersionStore.getState();
    return !isApp ? 'web' : os.isIos ? 'ios' : os.isAndroid ? 'android' : '';
};

axios.interceptors.request.use((config) => {
    if (isOwnApi(config.url)) {
        const { isApp, versionParam } = useAppVersionStore.getState();
        const platform = currentPlatform();
        config.headers = { ...config.headers, ...(platform ? { 'X-Mirinae-Platform': platform } : {}) };
        if (isApp && versionParam) config.headers['X-Mirinae-App-Version'] = String(versionParam);
    }
    return config;
});

// fire and forget: an event never delays or breaks the page
export const sendEvent = (name, app, props) => {
    axios.post('/api/events', { name, app, props }).catch(() => {});
};

// which app a page belongs to, from its first path segment
const APP_BY_ROOT = { explorer: 'explorer', lessons: 'lessons', wikinae: 'wikinae', 'lyric-explorer': 'lyrics', members: 'members' };
export const currentApp = () => APP_BY_ROOT[window.location.pathname.split('/')[1]] || 'explorer';

// the song a Lyric Explorer page shows, if any
const songOnPage = () => window.location.pathname.match(/^\/lyric-explorer\/song\/([0-9a-f]{24})/i)?.[1];

// a learner opened a word's definition or a grammar pattern: the word in dictionary form, or the
// pattern's id and reference form. Never the sentence it came from.
export const sendItemOpened = (itemType, key, extra = {}) => {
    if (!key) return;
    const songId = songOnPage();
    sendEvent('item_opened', currentApp(), { itemType, key: String(key).slice(0, 60), ...extra, ...(songId ? { songId } : {}) });
};

// ---- time on page ----
// Where learners spend their time: seconds each area is actually in use, reported as page_time when the
// learner moves to another area, leaves or hides the page, and at least once a minute. A second counts
// only while the page is visible and someone used it in the last IDLE_MS (or a video has focus), so a
// phone left on the table or a background tab adds nothing.
const IDLE_MS = 3 * 60 * 1000;
const FLUSH_SECONDS = 60;

// the page within an area, for the admin's page breakdown (#98): the app, its page when it is one of the app's own
// routes (each app's modules/defines/paths.js), and ':id' for an id. Anything else becomes '*', so a token, search
// term or other text in an address never reaches the record.
const PAGES = {
    explorer: ['reference', 'library', 'glossary', 'help', 'about', 'history', 'tutorial', 'ocr', 'user-feedback', 'userFeedback', 'landing'],
    lessons: ['active', 'advanced', 'beginner', 'complete', 'course', 'features', 'idiom', 'intermediate', 'landing', 'mylessons', 'neologism', 'overview', 'player', 'proverb', 'question', 'starter', 'summary', 'survey'],
    wikinae: ['bookmark', 'entries', 'entry', 'history', 'profile', 'comments', 'search'],
    'lyric-explorer': ['artist', 'artists', 'group', 'listing', 'music', 'song'],
    members: ['account-setting', 'cancel-subscription', 'payment', 'plan', 'profile', 'promotion', 'subscription'],
    help: [],
};
const OID = /^[0-9a-f]{24}$/i;
export const pageOf = (path) => {
    const [, root = '', sub = '', id = ''] = path.split('/');
    if (!root) return '/';
    if (!Object.prototype.hasOwnProperty.call(PAGES, root)) return '/*';
    const page = !sub ? '' : PAGES[root].includes(sub) ? sub : OID.test(sub) ? ':id' : '*';
    return `/${[root, page, page !== ':id' && OID.test(id) ? ':id' : ''].filter(Boolean).join('/')}`;
};

// the area a path belongs to, plus the lesson or song it shows and the page; null for pages that are not learning
// (sign-in, admin)
export const areaOf = (path) => {
    const where = areaOnly(path);
    return where && { ...where, page: pageOf(path) };
};
const areaOnly = (path) => {
    const [, root = '', sub = '', id = ''] = path.split('/');
    const oid = /^[0-9a-f]{24}$/i;
    switch (root) {
        case 'explorer': return { area: sub === 'landing' ? 'landing' : 'analyzer' };
        case 'lessons': return sub === 'player' && oid.test(id) ? { area: 'lesson', lessonId: id } : { area: 'lessons' };
        case 'wikinae': return { area: 'wikinae' };
        case 'lyric-explorer': return sub === 'song' && oid.test(id) ? { area: 'song', songId: id } : { area: 'songs' };
        case 'members': return { area: 'account' };
        case 'help': return { area: 'help' };
        case '': return { area: 'landing' };
        default: return null;   // auth, admin and anything else
    }
};

// keepalive lets the last report out while the page is closing, and still carries our headers
const sendTime = (where, seconds) => {
    if (!where || seconds < 1) return;
    const { isApp, versionParam } = useAppVersionStore.getState();
    const platform = !isApp ? 'web' : os.isIos ? 'ios' : os.isAndroid ? 'android' : '';
    fetch('/api/events', {
        method: 'POST',
        keepalive: true,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            ...(platform ? { 'X-Mirinae-Platform': platform } : {}),
            ...(isApp && versionParam ? { 'X-Mirinae-App-Version': String(versionParam) } : {}),
        },
        body: JSON.stringify({ name: 'page_time', app: currentApp(), props: { ...where, seconds: Math.round(seconds) } }),
    }).catch(() => {});
};

// a song's video is playing (set by the lyrics player from the YouTube player's state)
let videoPlaying = false;
export const setVideoPlaying = (on) => { videoPlaying = !!on; };

// Only the tab last used counts, so two windows open side by side do not count the same minute twice.
// ponytail: a localStorage marker "<tab>:<time>". A tab claims it only while visible and gives it up when
// hidden or closed; a claim older than the idle limit (a crashed tab) no longer blocks anyone. Without
// storage (private mode) every visible tab counts, and the server's time budget still caps the total.
const TAB_KEY = 'mirinae.activeTab';
const tabId = Math.random().toString(36).slice(2);
let claimedAt = 0;
const claimTab = () => {
    if (document.visibilityState !== 'visible') return;
    try {
        // rewrite at most every 10 s while this tab holds it; at once when another tab took it
        if (Date.now() - claimedAt < 10000 && (localStorage.getItem(TAB_KEY) || '').startsWith(`${tabId}:`)) return;
        localStorage.setItem(TAB_KEY, `${tabId}:${Date.now()}`); claimedAt = Date.now();
    } catch (e) { /* no storage */ }
};
const releaseTab = () => {
    claimedAt = 0;
    try { if ((localStorage.getItem(TAB_KEY) || '').startsWith(`${tabId}:`)) localStorage.removeItem(TAB_KEY); } catch (e) { /* no storage */ }
};
const isActiveTab = () => {
    try {
        const [id, at] = (localStorage.getItem(TAB_KEY) || '').split(':');
        return !id || id === tabId || Date.now() - Number(at) > IDLE_MS;
    } catch (e) { return true; }
};

const startTimeTracking = () => {
    if (typeof window === 'undefined' || window.__mirinaePageTime) return;   // once per page, whatever imports this
    window.__mirinaePageTime = true;
    let lastInput = Date.now();
    let path = window.location.pathname;
    let where = areaOf(path);
    let seconds = 0;

    const flush = () => { sendTime(where, seconds); seconds = 0; };
    const used = () => { lastInput = Date.now(); claimTab(); };
    ['pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel'].forEach(t => window.addEventListener(t, used, { passive: true, capture: true }));
    claimTab();

    // ponytail: a one-second tick (cheap; no router hooks needed, so it works in every app as is)
    setInterval(() => {
        if (window.location.pathname !== path) {   // moved within the app
            flush();
            path = window.location.pathname;
            where = areaOf(path);
        }
        const inUse = (Date.now() - lastInput < IDLE_MS && isActiveTab()) || videoPlaying;
        if (document.visibilityState === 'visible' && inUse) seconds += 1;
        if (seconds >= FLUSH_SECONDS) flush();
    }, 1000);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') { flush(); releaseTab(); } else claimTab(); });
    window.addEventListener('pagehide', () => { flush(); releaseTab(); });
};
startTimeTracking();
